import {
  Component,
  OnInit,
  computed,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { merge, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import * as fromSharedUi from '@sandbox-platform/shared-ui';
import { AuthStore, PERMISSIONS } from '@sandbox-platform/shared-ui';

import { AttendanceSettingDialogStore } from './+state/attendance-setting-dialog.store';
import { AttendanceAutoCloseLogStore } from './+state/attendance-auto-close-log.store';
import { AttendanceSettingsCurrentStateComponent } from './components/attendance-settings-current-state.component';
import { AttendanceAutoCloseLogsTabComponent } from './components/attendance-auto-close-logs-tab.component';
import { AttendanceSettingsDetailFormService } from './attendance-settings-detail.form';
import { AttendanceSettingsDTO } from '../attendance-settings.types';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AttendanceSettingsCurrentStateComponent,
    AttendanceAutoCloseLogsTabComponent,
    fromSharedUi.TextInputComponent,
    fromSharedUi.SubmitButtonComponent,
    fromSharedUi.ErrorAlertComponent,
    fromSharedUi.FeatureHeaderComponent,
    fromSharedUi.TabsNavigationComponent,
  ],
  providers: [AttendanceSettingsDetailFormService],
  selector: 'lib-attendance-settings-detail',
  templateUrl: './attendance-settings-detail.component.html',
  host: {
    class: 'block h-full min-h-0',
  },
})
export class AttendanceSettingsDetailComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly formService = inject(AttendanceSettingsDetailFormService);
  protected readonly authStore = inject(AuthStore);
  readonly store = inject(AttendanceSettingDialogStore);
  readonly logStore = inject(AttendanceAutoCloseLogStore);

  readonly tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'auto-close-logs', label: 'Auto-Close Logs' },
  ];
  readonly activeTab = signal<string>('settings');

  readonly form: FormGroup = this.formService.createForm();
  private readonly formChanges: Signal<unknown>;
  readonly formSubmitted = signal(false);
  private readonly initialValue = signal<string>('');

  protected readonly canCreate = computed(() =>
    this.authStore.hasExpertPermission(PERMISSIONS.EXPERT.ATTENDANCE_CREATE)
  );
  protected readonly canUpdate = computed(() =>
    this.authStore.hasExpertPermission(PERMISSIONS.EXPERT.ATTENDANCE_UPDATE)
  );
  protected readonly canRead = computed(() =>
    this.authStore.hasExpertPermission(PERMISSIONS.EXPERT.ATTENDANCE_READ)
  );
  protected readonly canSave = computed(
    () =>
      this.canUpdate() ||
      (!this.store.hasPersistedSettings() && this.canCreate())
  );
  protected readonly autoCloseEnabled = computed(() => {
    this.formChanges();
    return this.form.get('enable_auto_close_open_attendance')?.value === true;
  });
  protected readonly autoCloseTimeError = computed(() => {
    this.formChanges();
    const control = this.form.get('auto_close_time');
    if (!control || !this.formSubmitted()) {
      return '';
    }

    const value = `${control.value ?? ''}`.trim();
    if (this.autoCloseEnabled() && !value) {
      return 'Auto close time is required when automatic closing is enabled';
    }
    if (value && control.errors?.['pattern']) {
      return 'Auto close time must be in HH:MM format';
    }

    return '';
  });

  constructor() {
    this.formChanges = toSignal(
      merge(this.form.valueChanges, this.form.statusChanges).pipe(
        startWith(this.form.getRawValue())
      ),
      { initialValue: this.form.getRawValue() }
    );
  }

  async ngOnInit(): Promise<void> {
    if (!this.canRead()) {
      this.store.setSettings(null);
      return;
    }

    const settings = await this.store.get();
    if (settings) {
      this.applySettingsToForm(settings);
    }
  }

  async onSave(): Promise<void> {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();

    const autoCloseTimeControl = this.form.get('auto_close_time');
    const autoCloseTime = `${autoCloseTimeControl?.value ?? ''}`.trim();
    if (
      this.form.get('enable_auto_close_open_attendance')?.value === true &&
      !autoCloseTime
    ) {
      autoCloseTimeControl?.setErrors({
        ...(autoCloseTimeControl.errors ?? {}),
        requiredWhenEnabled: true,
      });
    } else if (autoCloseTimeControl?.hasError('requiredWhenEnabled')) {
      const { requiredWhenEnabled, ...otherErrors } =
        autoCloseTimeControl.errors ?? {};
      autoCloseTimeControl.setErrors(
        Object.keys(otherErrors).length ? otherErrors : null
      );
    }

    if (this.form.invalid) {
      return;
    }

    if (!this.canSave()) {
      return;
    }

    await this.store.save({
      createDto: this.formService.getCreateDTO(this.form),
      updateDto: this.formService.getUpdateDTO(this.form),
      useUpdate: this.canUpdate() || this.store.hasPersistedSettings(),
      afterSave: (settings) => {
        this.applySettingsToForm(settings);
        this.formSubmitted.set(false);
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  onTabChange(tabId: string): void {
    this.activeTab.set(tabId);
    if (tabId === 'auto-close-logs') {
      this.logStore.loadLogs(15);
    }
  }

  onAutoCloseToggle(event: Event): void {
    const target = event.target as HTMLInputElement;
    const enabled = target.checked;
    this.form.patchValue({
      enable_auto_close_open_attendance: enabled,
      auto_close_time: enabled ? this.form.get('auto_close_time')?.value : '',
    });
    this.form.markAsDirty();
    this.form.get('enable_auto_close_open_attendance')?.markAsDirty();

    if (!enabled) {
      this.form.get('auto_close_time')?.setErrors(null);
    }
  }

  private applySettingsToForm(settings: AttendanceSettingsDTO): void {
    this.store.setSettings(settings);
    this.formService.populateForm(this.form, settings);
    this.initialValue.set(this.serializeFormValue());
    this.form.markAsPristine();
  }

  private serializeFormValue(): string {
    return JSON.stringify(this.form.getRawValue());
  }
}
